import Layout from "@/components/layout/Layout";
import PageHero from "@/components/shared/PageHero";
import PricingTable from "@/components/shared/PricingTable";
import FAQSection from "@/components/home/FAQSection";
import PartnersSection from "@/components/home/PartnersSection";
import SEOHead from "@/components/seo/SEOHead";
import SchemaMarkup from "@/components/seo/SchemaMarkup";
import { Server, Users, Shield, Headphones, HardDrive, Globe } from "lucide-react";

const packages = [
  {
    name: "Starter",
    originalPrice: "544",
    price: "489",
    period: "/month",
    description: "Start your BDIX hosting business",
    features: [
      "10 cPanel Accounts",
      "10 GB NVMe SSD",
      "200 GB Bandwidth",
      "Free SSL Certificates",
      "WHM Access",
      "White-label Branding",
      "Private Nameservers",
      "Priority Support",
    ],
    orderLink: "https://my.webhostbuybd.com/store/reseller-hosting/starter",
  },
  {
    name: "Advanced",
    originalPrice: "1,152",
    price: "979",
    period: "/month",
    description: "Growing reseller business",
    features: [
      "25 cPanel Accounts",
      "25 GB NVMe SSD",
      "500 GB Bandwidth",
      "Free SSL Certificates",
      "WHM Access",
      "White-label Branding",
      "Private Nameservers",
      "Priority Support",
    ],
    orderLink: "https://my.webhostbuybd.com/store/reseller-hosting/advanced",
  },
  {
    name: "Business",
    originalPrice: "1,837",
    price: "1,469",
    period: "/month",
    description: "Professional resellers",
    features: [
      "50 cPanel Accounts",
      "50 GB NVMe SSD",
      "1000 GB Bandwidth",
      "Free SSL Certificates",
      "WHM Access",
      "White-label Branding",
      "Private Nameservers",
      "Priority Support",
    ],
    popular: true,
    orderLink: "https://my.webhostbuybd.com/store/reseller-hosting/business",
  },
  {
    name: "Professional",
    originalPrice: "2,666",
    price: "1,999",
    period: "/month",
    description: "High-volume resellers",
    features: [
      "100 cPanel Accounts",
      "100 GB NVMe SSD",
      "Unlimited Bandwidth",
      "Free SSL Certificates",
      "WHM Access",
      "White-label Branding",
      "Private Nameservers",
      "Priority Support",
      "WHMCS License",
    ],
    orderLink: "https://my.webhostbuybd.com/store/reseller-hosting/professional",
  },
  {
    name: "Enterprise",
    originalPrice: "4,270",
    price: "2,989",
    period: "/month",
    description: "Large hosting agencies",
    features: [
      "200 cPanel Accounts",
      "200 GB NVMe SSD",
      "Unlimited Bandwidth",
      "Free SSL Certificates",
      "WHM Access",
      "White-label Branding",
      "Private Nameservers",
      "Priority Support",
      "WHMCS License",
    ],
    orderLink: "https://my.webhostbuybd.com/store/reseller-hosting/enterprise",
  },
  {
    name: "Ultimate",
    originalPrice: "8,332",
    price: "4,999",
    period: "/month",
    description: "Maximum capacity",
    features: [
      "500 cPanel Accounts",
      "500 GB NVMe SSD",
      "Unlimited Bandwidth",
      "Free SSL Certificates",
      "WHM Access",
      "White-label Branding",
      "Private Nameservers",
      "Priority Support",
      "WHMCS License",
    ],
    orderLink: "https://my.webhostbuybd.com/store/reseller-hosting/ultimate",
  },
];

const features = [
  { icon: Users, title: "WHM Control", description: "Full reseller management" },
  { icon: HardDrive, title: "NVMe SSD", description: "Enterprise BDIX storage" },
  { icon: Shield, title: "Free SSL", description: "All accounts included" },
  { icon: Server, title: "LiteSpeed", description: "High-performance servers" },
  { icon: Globe, title: "99.99% Uptime", description: "Guaranteed availability" },
  { icon: Headphones, title: "24/7 Support", description: "Expert Bangladesh team" },
];

const resellerFaqs = [
  {
    question: "What is BDIX hosting and why is it important?",
    answer: "BDIX (Bangladesh Internet Exchange) hosting provides ultra-fast connectivity within Bangladesh by routing traffic through local exchange points. This results in significantly lower latency and faster page load times for visitors within Bangladesh, making it ideal for local businesses and websites targeting Bangladeshi audiences.",
  },
  {
    question: "Do you offer free website migration?",
    answer: "Yes! We offer completely free website migration for all our hosting plans. Our expert team will migrate your website, databases, emails, and all files from your current host to our servers with zero downtime.",
  },
  {
    question: "What control panel do you use?",
    answer: "We use cPanel, the industry's most popular and user-friendly control panel. It provides an intuitive interface for managing your website, emails, databases, files, and more. We also offer one-click installers for WordPress, Joomla, and 400+ other applications.",
  },
  {
    question: "What is your uptime guarantee?",
    answer: "We guarantee 99.99% uptime for all our hosting services, backed by our Service Level Agreement (SLA). Our state-of-the-art datacenter infrastructure, redundant power systems, and 24/7 monitoring ensure your website stays online.",
  },
  {
    question: "Do you provide daily backups?",
    answer: "Yes, we perform automated daily backups of all hosting accounts. Backups are stored on separate servers for added security. You can also create manual backups anytime through cPanel or request restoration from our support team.",
  },
  {
    question: "How fast is your customer support?",
    answer: "Our customer support team is available 24/7 via live chat, phone, and ticket system. Average response time for live chat is under 2 minutes, and most tickets are resolved within 1 hour. Our support team consists of certified hosting experts.",
  },
];

const ResellerHosting = () => {
  return (
    <Layout>
       <SEOHead
         title="Cheapest Reseller Hosting BD | BDIX Reseller Hosting with WHM &amp; White-Label"
         description="Cheapest reseller hosting BD from ৳489/month. BDIX reseller hosting Bangladesh with WHM access, white-label branding &amp; private nameservers. Start your hosting business in Bangladesh. BDIX hosting reseller with 24/7 support. Web Host Buy BD."
         canonical="/bdix-reseller-hosting"
         type="product"
         keywords="BDIX reseller hosting, reseller hosting Bangladesh, cheapest reseller hosting BD, white-label hosting, reseller hosting provider, cheap web hosting reseller, bdix cloud hosting, bdix hosting reseller, bdix reseller hosting, affordable web hosting Bangladesh, Web Host Buy BD, web host bd"
       />
      <SchemaMarkup
        type="product"
        breadcrumbs={[
          { name: "Home", url: "/" },
          { name: "BDIX Reseller Hosting", url: "/bdix-reseller-hosting" },
        ]}
        faqs={resellerFaqs}
        products={packages.map((p) => ({
          name: `${p.name} BDIX Reseller Hosting BD`,
          description: `${p.description} - Cheapest reseller hosting BD with WHM access`,
          price: p.price,
        }))}
        pageName="Cheapest Reseller Hosting BD - BDIX Reseller Hosting Bangladesh"
        pageDescription="Cheapest reseller hosting BD, BDIX reseller hosting, reseller hosting Bangladesh. Host buy BD, web host BD from ৳489/month."
      />
      <PageHero
        badge="Cheapest Reseller Hosting BD"
        title="Cheapest Reseller Hosting in Bangladesh"
        subtitle="Start your web hosting business with BDIX reseller hosting. WHM access, white-label branding, and 24/7 expert support."
      />

      {/* Features Strip */}
      <section className="py-12 bg-muted/30 border-b border-border" aria-label="BDIX Reseller Hosting Features">
        <div className="container-custom">
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-6">
            {features.map((feature) => (
              <div
                key={feature.title}
                className="flex items-center gap-3"
              >
                <div className="w-10 h-10 rounded-lg bg-blue-50 flex items-center justify-center flex-shrink-0" aria-hidden="true">
                  <feature.icon className="w-5 h-5 text-primary" />
                </div>
                <div>
                  <div className="font-medium text-foreground text-sm">{feature.title}</div>
                  <div className="text-xs text-muted-foreground">{feature.description}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <PricingTable
        packages={packages}
        title="Choose Your BDIX Reseller Plan"
        subtitle="All BDIX plans include WHM access, white-label branding, and 24/7 technical support. Affordable hosting price in Bangladesh."
      />

      <FAQSection />
      <PartnersSection />
    </Layout>
  );
};

export default ResellerHosting;
